import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "@/hooks/useSearchParams";
import {
  validatePassword,
  validateEmail,
  validateText,
  validatePasswordEquality,
  validateMongoObjectId,
} from "@/lib/utils/input-validator";
import { useTranslations, useLocale } from "next-intl";
import { translateBackendError } from "@/lib/i18n/translateBackendError";
import { initiateRegistration } from "@/lib/api/handlers/AuthenticationHandling";
import { useLocale as useAppLocale } from "@/lib/i18n/I18nProvider";
import { useRateLimitCountdown } from "@/hooks/useRateLimitCountdown";
import { Locale } from "@/i18n/config";
import { throwServerError } from "@/lib/api/errors/ErrorHandler";
import { checkboxDocumentVersions } from "@/lib/legal/documents";

// Initial state for the registration form. D11 (AVV plan Rev. 6.5, P6a.4):
// this form takes invitations only — no company block (country, account type,
// tax ID) any more; a company is founded through the email-first door, where
// the AVV is concluded.
const initialState = {
  firstName: "",
  lastName: "",
  email: "",
  language: "",
  password: "",
  confirmPassword: "",
  inviteCode: "",
};

// Initial state for password validation errors
const errorState: {
  length?: boolean;
  uppercase?: boolean;
  lowercase?: boolean;
  specialChar?: boolean;
} = {
  length: undefined,
  uppercase: undefined,
  lowercase: undefined,
  specialChar: undefined,
};

// Type definition for form field names
type FormField = keyof typeof initialState;

/**
 * The invite form (`/auth/register?invite_code=…`). D11 (AVV plan Rev. 6.5,
 * P6a.4): invitations only. The "I have an invite code" switch and the
 * company lane behind it are gone — the server answers a body without an
 * invite code with 400 `inviteCodeRequired`; whoever wants an own company
 * takes the normal registration (the form links it).
 */
export function useInviteRegisterForm() {
  // Form state management
  const [formData, setFormData] = useState(initialState);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [passwordValidation, setPasswordValidation] = useState(errorState);
  const [isLoading, setIsLoading] = useState(false);
  // The consent tick (W9 1-S): terms+privacy. No DPA box — an invitee joins
  // an organisation whose DPA already stands (⚑A1), and since D11 this form
  // founds no company. The `*Invalid` half means "clicked Create without this
  // tick": set only by a failed click, cleared by the tick (⚑A2).
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsInvalid, setTermsInvalid] = useState(false);
  const termsRef = useRef<HTMLDivElement>(null);
  // The invite-code input: a failed click puts focus on it — it sits above
  // the box, so it is the first invalid control (audit lens-6 F10; since D11
  // it carries a visible label and is always required).
  const inviteCodeRef = useRef<HTMLInputElement>(null);
  const rateLimit = useRateLimitCountdown();
  const t = useTranslations("errors");
  const currentLocale = useLocale();
  const { setLocale } = useAppLocale();

  // Keep the countdown banner in sync with the ticking clock
  useEffect(() => {
    if (!rateLimit.isLimited) return;
    setFormErrors((prev) => ({
      ...prev,
      email: t("rateLimitedCountdown", { seconds: rateLimit.secondsLeft }),
    }));
  }, [rateLimit.secondsLeft, rateLimit.isLimited, t]);

  const navigate = useNavigate();
  const searchParams = useSearchParams();

  // Pre-populate language from the app's detected locale on mount
  useEffect(() => {
    setFormData((prev) => ({ ...prev, language: currentLocale }));
  }, [currentLocale]);

  // Handle invite_code query parameter on mount
  useEffect(() => {
    const inviteCodeFromUrl = searchParams.get("invite_code");
    if (inviteCodeFromUrl) {
      setFormData((prevState) => ({
        ...prevState,
        inviteCode: inviteCodeFromUrl,
      }));
    }
  }, [searchParams]);

  /**
   * Handles input change for form fields
   * Validates field on change and updates error states
   * @param field - The form field being updated
   * @param value - The new value for the field
   */
  const handleInputChange = (field: FormField, value: string) => {
    // Update form data with new value
    setFormData((prevState) => ({
      ...prevState,
      [field]: value,
    }));

    // Only validate if there's an existing error or for password fields
    if (
      formErrors[field] ||
      field === "confirmPassword" ||
      field === "password" ||
      field === "inviteCode"
    ) {
      let errorMessage = "";

      // Field-specific validation
      if (field === "email") {
        errorMessage = validateEmail(value, t);
      } else if (["firstName", "lastName"].includes(field)) {
        errorMessage = validateText(value, t);
      } else if (field === "password") {
        const validationState = validatePassword(value);
        setPasswordValidation(validationState);
      } else if (field === "confirmPassword") {
        errorMessage = validatePasswordEquality(formData.password, value, t);
      } else if (field === "inviteCode") {
        errorMessage = validateMongoObjectId(value);
        if (errorMessage) {
          errorMessage = t(errorMessage);
        }
      }

      // Update error state for this field
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [field]: errorMessage,
      }));
    }
  };

  /**
   * Handles language change - updates form state and app locale
   */
  const handleLanguageChange = async (locale: Locale) => {
    setFormData((prev) => ({ ...prev, language: locale }));
    setLocale(locale);
  };

  const handleTermsChange = (checked: boolean) => {
    setTermsAccepted(checked);
    if (checked) setTermsInvalid(false);
  };

  /**
   * Validates the entire form
   * @returns boolean indicating if form is valid
   */
  const isFormValid = () => {
    // Validate all fields and collect errors
    const errors: Record<string, string> = {
      firstName: validateText(formData?.firstName, t),
      lastName: validateText(formData.lastName, t),
      email: validateEmail(formData.email, t),
      confirmPassword: validatePasswordEquality(
        formData.password,
        formData.confirmPassword,
        t,
      ),
    };

    // D11: a code is REQUIRED — the server answers a body without one with
    // 400 `inviteCodeRequired` (a blank code is mapped to null there).
    if (formData.inviteCode.trim() === "") {
      errors.inviteCode = t("inviteCodeRequired");
    } else {
      errors.inviteCode = validateMongoObjectId(formData.inviteCode);
      if (errors.inviteCode) {
        errors.inviteCode = t(errors.inviteCode);
      }
    }

    // Validate password and update validation state
    setPasswordValidation(validatePassword(formData.password));
    setFormErrors(errors);

    // Consent: the terms box. A missing tick marks the box (visual + aria +
    // error line). Focus goes to the FIRST invalid control, in DOM order,
    // among the ones this pass marks: the invite code (above the box), then
    // the terms box.
    const termsMissing = !termsAccepted;
    setTermsInvalid(termsMissing);
    if (errors.inviteCode) inviteCodeRef.current?.focus();
    else if (termsMissing) termsRef.current?.focus();

    return !Object.values(errors).some((error) => error) && !termsMissing;
  };

  /**
   * Handles form submission
   * @param e - Form submission event
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Block while under rate-limit countdown
    if (rateLimit.isLimited) return;

    // Validate form before submission
    const formValid = isFormValid();
    if (!formValid) return;

    try {
      setIsLoading(true);

      // Call registration API with selected language. D11: always the invite
      // lane — `isFormValid` just refused a blank code — so no company
      // argument is ever sent from here.
      const response = await initiateRegistration(
        formData?.firstName,
        formData.lastName,
        formData.password,
        formData.email,
        formData.language || currentLocale,
        formData.inviteCode.trim(),
        undefined,
        undefined,
        undefined,
        // Proof of consent: which documents, in which version, TICKED (W9
        // 1-S) — the box that was on the screen: terms+privacy. Parked on the
        // pending registration and written to company_audit_logs at
        // activation as LEGAL_CONSENT_ACCEPTED.
        checkboxDocumentVersions(["terms", "privacy"]),
      );

      if (
        response &&
        "isServerError" in response &&
        response.isServerError === true
      ) {
        if (response.message === "rateLimited" && response.retryAfter) {
          rateLimit.start(response.retryAfter);
        }
        throwServerError(response);
      }

      // Redirect to check-email page on success
      window.dispatchEvent(new Event("navigationStart"));
      navigate("/auth/check-email");
    } catch (error) {
      // Handle registration errors
      let errorMessage = "unknown";
      if (error instanceof Error) errorMessage = error.message;

      if (errorMessage === "inviteCodeRequired") {
        // D11: the server takes invitations only on this door.
        setFormErrors((prevState) => ({
          ...prevState,
          inviteCode: t("inviteCodeRequired"),
        }));
        inviteCodeRef.current?.focus();
      } else if (errorMessage === "legalConsentIncomplete") {
        // The backend's own consent rule said no although every box here was
        // ticked (a lane the form did not expect, or a stale tab whose map
        // predates the rule). Said under the boxes, where the answer is.
        setFormErrors((prevState) => ({
          ...prevState,
          consent: t("legalConsentIncomplete"),
        }));
      } else {
        // Display default errors on the email field
        setFormErrors((prevState) => ({
          ...prevState,
          email: translateBackendError(t, error instanceof Error ? error : "unknown"),
        }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Return all form state and handlers
  return {
    formData,
    formErrors,
    passwordValidation,
    isLoading,
    handleInputChange,
    handleLanguageChange,
    handleSubmit,
    rateLimited: rateLimit.isLimited,
    termsAccepted,
    termsInvalid,
    handleTermsChange,
    termsRef,
    inviteCodeRef,
  };
}
